/**
 * PATH: packages/ui/src/hooks/useCloseOnOutsidePointer.ts
 * FILE_ID: UI-HOOK-CLOSE-OUTSIDE-001
 * PURPOSE: Close a floating menu on outside pointerdown or Escape.
 * WHY: Bubble mousedown misses a blank tap on a phone and any child that
 *      stops propagation. The listener exists only while the menu is open.
 */
import { useEffect, useRef } from 'react'

import { shouldDismissContextMenu } from '../kanban/hooks/shouldDismissContextMenu'

// Reused from: packages/ui/src/kanban/hooks/shouldDismissContextMenu.ts (shouldDismissContextMenu)
// Reused from: packages/ui/src/kanban/hooks/useDismissOnOutsideInteraction.ts (useDismissOnOutsideInteraction)
export function useCloseOnOutsidePointer(
  active: boolean,
  onClose: () => void,
  roots: ReadonlyArray<{ readonly current: HTMLElement | null }>,
): void {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const rootsRef = useRef(roots)
  rootsRef.current = roots

  useEffect(() => {
    if (!active) return
    let armed = false
    const arm = window.setTimeout(() => {
      armed = true
    }, 0)
    const onPointerDown = (event: PointerEvent) => {
      if (!armed) return
      const live = rootsRef.current
        .map((root) => root.current)
        .filter((node): node is HTMLElement => node != null)
      if (live.length === 0) return
      const outside = live.every((node) => shouldDismissContextMenu(node, event.target))
      if (outside) onCloseRef.current()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(arm)
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [active])
}
